import React, { useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Avatar,
  Typography,
  Divider,
  Tabs,
  Tab,
  Box,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import { UsuarioIncompleto } from "../../Hooks/UsuarioHook";
import { useEntidade, Entidade } from "../../Hooks/EntidadeHook";

interface Props {
  open: boolean;
  onClose: () => void;
  titulo: string;
  subtitulo: string;
  contas: UsuarioIncompleto[];
  onSelecionar: (conta: UsuarioIncompleto) => void;
  loading?: boolean;
}

const normalizarTexto = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// deduz o tipo/estilo da entidade a partir do título
export function getTipoEntidade(titulo: string) {
  const t = normalizarTexto(titulo);
  if (t.includes("camara"))
    return { label: "Câmara", color: "#7c3aed", Icon: AccountBalanceIcon };
  if (t.includes("consorcio"))
    return { label: "Consórcio", color: "#0d9488", Icon: Diversity3Icon };
  return { label: "Prefeitura", color: "#2563eb", Icon: ApartmentIcon };
}

export const SeletorContasModal: React.FC<Props> = ({
  open,
  onClose,
  titulo,
  subtitulo,
  contas,
  onSelecionar,
  loading = false,
}) => {
  const { entidades } = useEntidade();

  const getEntidade = (entidadeId: number): Entidade | undefined =>
    entidades.find((e: Entidade) => e.id === entidadeId);

  const [pesquisaEntidade, setPesquisaEntidade] = useState("");
  const [filtroEntidade, setFiltroEntidade] = useState<
    "prefeitura" | "camara" | "consorcio" | "todos"
  >("todos");

  const contasFiltradas = useMemo(() => {
    return contas.filter((conta) => {
      const entidade = entidades.find((e) => e.id === conta.entidade_id);
      if (!entidade) return false;

      const tituloNormalizado = normalizarTexto(entidade.titulo);

      if (filtroEntidade !== "todos") {
        if (filtroEntidade === "prefeitura" && !tituloNormalizado.includes("prefeitura"))
          return false;
        if (filtroEntidade === "camara" && !tituloNormalizado.includes("camara"))
          return false;
        if (filtroEntidade === "consorcio" && !tituloNormalizado.includes("consorcio"))
          return false;
      }

      if (pesquisaEntidade) {
        const pesquisaNormalizada = normalizarTexto(pesquisaEntidade);
        if (!tituloNormalizado.includes(pesquisaNormalizada)) return false;
      }

      return true;
    });
  }, [contas, entidades, filtroEntidade, pesquisaEntidade]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Saudação */}
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Typography variant="h5" fontWeight={800}>
          {titulo}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitulo}
        </Typography>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Filtros por tipo */}
        <Tabs
          value={filtroEntidade}
          onChange={(_, value) => setFiltroEntidade(value)}
          centered
          sx={{ mb: 2 }}
        >
          <Tab label="Prefeitura" value="prefeitura" />
          <Tab label="Câmara" value="camara" />
          <Tab label="Consórcio" value="consorcio" />
          <Tab label="Todos" value="todos" />
        </Tabs>

        {/* Busca */}
        <TextField
          fullWidth
          size="small"
          placeholder="Pesquisar entidade..."
          value={pesquisaEntidade}
          onChange={(e) => setPesquisaEntidade(e.target.value)}
          sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Conteúdo com altura fixa */}
        {loading ? (
          <Box
            sx={{
              height: 520,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : contasFiltradas.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gridAutoRows: "min-content",
              gap: 1.5,
              height: 520,
              overflowY: "auto",
              pr: 0.5,
              pb: 0.5,
            }}
          >
            {contasFiltradas.map((conta: UsuarioIncompleto) => {
              const entidade = getEntidade(conta.entidade_id);
              const tituloEnt = entidade?.titulo ?? "Entidade desconhecida";
              const tipo = getTipoEntidade(tituloEnt);

              return (
                <Box
                  key={conta.id}
                  onClick={() => onSelecionar(conta)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 1,
                    p: 2,
                    borderRadius: 3,
                    border: 1,
                    borderColor: "divider",
                    cursor: "pointer",
                    transition: "all .18s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "action.hover",
                      transform: "translateY(-3px)",
                      boxShadow: "0 8px 22px rgba(37,99,235,0.18)",
                    },
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={entidade?.logo}
                    alt={tituloEnt}
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: "background.default",
                      color: tipo.color,
                      border: 1,
                      borderColor: "divider",
                      "& img": { objectFit: "contain" },
                    }}
                  >
                    <tipo.Icon fontSize="large" />
                  </Avatar>

                  <Chip
                    size="small"
                    label={tipo.label}
                    sx={{
                      height: 20,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      color: tipo.color,
                      bgcolor: `${tipo.color}1A`,
                    }}
                  />

                  <Typography
                    fontWeight={700}
                    sx={{
                      lineHeight: 1.25,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {tituloEnt}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
                    {conta.nome}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              height: 520,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Nenhuma conta encontrada nesta categoria.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ justifyContent: "center", py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ px: 4, borderRadius: 2 }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
